import { useState } from "react";
import { SAVE_STORAGE_KEY } from "../game/index.js";
import type { GameController } from "../shell/hooks/useGameController.js";
import { IntroReelStack } from "../shell/ui/IntroReelStack.js";
import { AboutModal } from "./AboutModal.js";

/**
 * Landing intro: always the first screen. A single "Continue your story" CTA
 * opens the Desk roadmap (OPEN_ROADMAP) — the hub where you pick a story/crowd,
 * so first play and returning play share one entry point. (The old two-button
 * "Start your first story" + "Your desk" split is gone.)
 */
export function HomeScreen({ game }: { game: GameController }) {
  const { dispatch } = game;
  const [aboutOpen, setAboutOpen] = useState(false);

  const startOver = () => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(SAVE_STORAGE_KEY);
    } catch {
      /* storage unavailable — reload still resets in-memory session */
    }
    window.location.reload();
  };

  return (
    <div className="desk-app desk-chrome home-intro-screen" data-testid="screen-home">
      <section className="home-intro-hero" data-testid="home-intro">
        <div className="home-logo">
          <span className="home-logo-mark" aria-hidden>
            <span className="home-logo-dot" />
          </span>
          <span className="home-logo-word">DESK</span>
        </div>

        <div className="home-intro-center">
          <IntroReelStack />
          <div className="home-intro-copy">
            <h1 className="home-headline">
              Make it viral.
              <span className="home-headline-accent">Don&apos;t get played.</span>
            </h1>
            <p className="home-intro-support">
              You&apos;re the new editor at DESK.
              <br />
              Same footage, a hundred ways to spin it.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="primary home-intro-cta"
          data-testid="start-shift"
          onClick={() => dispatch({ type: "OPEN_ROADMAP" })}
        >
          Continue your story
        </button>

        <div className="home-intro-footlinks">
          <button
            type="button"
            className="home-start-over"
            data-testid="start-over"
            onClick={startOver}
          >
            Start over
          </button>
          <span className="home-footlink-sep" aria-hidden>
            ·
          </span>
          <button
            type="button"
            className="home-start-over home-about-link"
            data-testid="open-about"
            onClick={() => setAboutOpen(true)}
          >
            About
          </button>
        </div>
      </section>

      {aboutOpen ? <AboutModal onClose={() => setAboutOpen(false)} /> : null}
    </div>
  );
}
